import { create } from 'zustand';
import { createAuthSlice, AuthSlice } from './slices/authSlice';
import { createChoresSlice, ChoresSlice } from './slices/choreSlice';
import { createInvitationSlice, InvitationSlice } from './slices/invitationSlice';
import { createGoogleTasksSlice, GoogleTasksSlice } from './slices/googleTasksSlice';
import { createLicenceSlice, LicenceSlice } from './slices/licenceSlice';
import authService from '../services/auth';
import { localModeService } from '../services/localModeService';
import { localUser, User } from '../domain';

// 1. Extend the store type to include the root init
export type AppStore = AuthSlice & ChoresSlice & InvitationSlice & GoogleTasksSlice & LicenceSlice & {
  initApp: () => () => void; // Returns a cleanup function
};

export const useAppStore = create<AppStore>()((...args) => {
  const [set, get] = args;

  return {
    ...createAuthSlice(...args),
    ...createChoresSlice(...args),
    ...createInvitationSlice(...args),
    ...createGoogleTasksSlice(...args),
    ...createLicenceSlice(...args),

    // 2. The Global Orchestrator
    initApp: () => {
      const updateOnlineStatus = () => {
          // We can update the sync status in the chores slice directly from here
          get().chores.updateSyncStatus({ isOnline: navigator.onLine });
        };

      window.addEventListener('online', updateOnlineStatus);
      window.addEventListener('offline', updateOnlineStatus);      

      // Signed in: wait until the sign-in has been completed, then switch to the account. If it's
      // gone meanwhile, the signed-out report that follows leads back to local mode.
      //
      // What was made in local mode is offered to the account, never added unasked: whoever signs
      // in may not be who used the phone. It's offered only on a sign-in this tab let in, or a
      // session already there when the app starts. Another tab's sign-in is reported here too,
      // before that tab has turned it away or not, and the data mustn't go into an account about to
      // be deleted; the tab that let it in asks.
      let firstReport = true;
      const enterAccount = async (user: User, isFirstReport: boolean) => {
        await authService.awaitSignInCheck();
        if (authService.currentUid() !== user.uid) return;

        // The households are replaced by load, not emptied first, so the dashboard doesn't show "no
        // households" on the way. Local mode's offer to undo a completion is no use to the account.
        get().chores.dismissUndoableCompletion();
        get().googleTasks.reset();
        await get().auth.updateUser(user, 'account');
        await get().chores.load();
        await get().invitations.load();

        if (isFirstReport || authService.admittedHere(user.uid)) {
          const local = await localModeService.countLocalData();
          if (local.households > 0) get().auth.offerLocalData(local);
        }
      };

      // Signed out, or never signed in: the app goes on without an account, on the phone alone
      // (docs/plans/local-mode.md), as the local user kept on this phone or a new one.
      const enterLocalMode = async () => {
        // Essential for security/privacy
        await get().chores.reset();
        get().invitations.reset();
        get().googleTasks.reset();
        const identity = await localModeService.getOrCreateIdentity();
        await get().auth.updateUser(localUser(identity), 'local');
        await get().chores.load();
      };

      // One report at a time, in order: a sign-in turned away is reported signed in and then signed
      // out, and the second must not overtake the first.
      let settled = Promise.resolve();
      const unsubscribeAuth = authService.onAuthStateChanged(
        (user) => {
          const isFirstReport = firstReport;
          firstReport = false;
          settled = settled
            .then(() => (user ? enterAccount(user, isFirstReport) : enterLocalMode()))
            .catch(async (error) => {
              console.error('Auth state change error:', error);
              set((state) => ({
                auth: { ...state.auth, loading: false, error: 'Failed to initialize authentication' },
              }));
              // Signed into Firebase but not into the app: sign out again, so the two agree and
              // signing in can be tried again. The signed-out report that follows leads back to
              // local mode.
              if (user) await authService.signOut().catch(() => undefined);
            });
        },
        (error) => {
          console.error('Auth state change error:', error);
          set((state) => ({
            auth: {
              ...state.auth,
              loading: false,
              error: 'Failed to initialize authentication',
            },
          }));
        }
      );

      // Unified Cleanup
      return () => {
        window.removeEventListener('online', updateOnlineStatus);
        window.removeEventListener('offline', updateOnlineStatus);
        unsubscribeAuth();
      };;
    },
  };
});